/** Point en coordonnées monde (mêmes unités que le viewBox 0..gridSize). */
export interface Point {
  x: number
  y: number
}

const EPS = 0.0001

/**
 * Cap (en degrés) d'origine vers destination, pour une coque SVG dessinée
 * pointant vers l'EST (+x) à l'angle 0. Le repère SVG a y vers le bas et
 * `rotate()` tourne dans le sens horaire : est=0, sud=+90, ouest=±180, nord=−90.
 */
export function headingAngle(origin: Point, dest: Point): number {
  return (Math.atan2(dest.y - origin.y, dest.x - origin.x) * 180) / Math.PI
}

/**
 * Longueur « constante à l'écran » sous le scale CSS `k` : une base divisée par
 * `k` garde la taille perçue fixe pendant que la couche transform zoome. Garde
 * contre `k ≤ 0`.
 */
export function screenConstant(base: number, k: number): number {
  return base / Math.max(k, EPS)
}

/** Les noms d'îles n'apparaissent qu'au-delà d'un seuil de zoom (anti-fouillis). */
export function shouldShowName(k: number, threshold = 1.6): boolean {
  return k >= threshold
}

/**
 * Transform CSS d'un marqueur d'île : centré sur son point via
 * `translate(-50%,-50%)`, contre-scalé `1/k` pour rester ~36px à l'écran. Une île
 * sélectionnée « ressort » à 1.25× (toujours rapporté au contre-scale).
 */
export function islandMarkerTransform(k: number, isActive: boolean): string {
  const s = (isActive ? 1.25 : 1) / Math.max(k, EPS)
  return `translate(-50%,-50%) scale(${s})`
}

export interface Cluster<T> {
  x: number
  y: number
  items: T[]
}

/**
 * Regroupement spatial glouton en unités MONDE. Deux marqueurs à distance
 * euclidienne ≤ `threshold` rejoignent le même cluster ; l'ancre du cluster est
 * le centroïde courant de ses membres. Déterministe (premier-arrivé, ordre
 * d'entrée préservé) → testable. Le seuil dépendant du zoom est calculé par
 * l'appelant (cf. RouteLayer : `FLEET_HULL_UNITS / k`).
 */
export function groupOverlapping<T extends Point>(markers: T[], threshold: number): Cluster<T>[] {
  const clusters: Cluster<T>[] = []
  for (const m of markers) {
    const hit = clusters.find((c) => Math.hypot(c.x - m.x, c.y - m.y) <= threshold)
    if (hit) {
      hit.items.push(m)
      hit.x = hit.items.reduce((s, it) => s + it.x, 0) / hit.items.length
      hit.y = hit.items.reduce((s, it) => s + it.y, 0) / hit.items.length
    } else {
      clusters.push({ x: m.x, y: m.y, items: [m] })
    }
  }
  return clusters
}
